/** The one object that owns an `AudioContext`.
 *
 * Everything a browser makes awkward about audio is handled here and nowhere
 * else:
 *
 *  - **A context cannot exist before a gesture.** Every modern browser starts a
 *    context suspended and refuses to resume it outside a user gesture, so the
 *    engine is constructed eagerly and *opened* lazily: `unlock()` is called
 *    from the first click or keypress the shell sees, and until then every
 *    `play` is a no-op rather than an error.
 *  - **A context is expensive and there must be exactly one.** React in strict
 *    mode mounts twice; a hot reload mounts again. `close()` is idempotent and
 *    the engine is safe to construct and throw away.
 *  - **There is no audio in a test.** `AudioContext` is undefined in node, so
 *    the constructor takes what it can get and every method checks. A headless
 *    run neither throws nor makes noise.
 *
 * Volumes are held here rather than read from settings: the host owns the
 * preference and pushes it in, the same way it owns the autosave preference.
 */

import type { Bus, Cue, CueHit } from "./cues.js";
import { BUS_OF, selectCues } from "./cues.js";
import type { Playlist, Scene, Track } from "./music.js";
import { SCORES, bar, toPlaylist } from "./music.js";
import type { Patch } from "./synth.js";
import { PATCHES, makeNoise, patchLength, playPatch } from "./synth.js";
import type { GameEvent } from "@game/core";

export interface AudioVolumes {
  readonly master: number;
  readonly sfx: number;
  readonly music: number;
}

/**
 * Music ships on but quiet. Nothing sounds until the player's first click
 * anyway - a browser will not open an `AudioContext` before a gesture - so
 * shipping it off would only mean most players never discovered there was any.
 */
export const DEFAULT_VOLUMES: AudioVolumes =
  Object.freeze({ master: 0.7, sfx: 0.8, music: 0.35 });

/** The four steps the settings screen offers, low to high. */
export const VOLUME_STEPS: readonly { readonly id: string; readonly value: number }[] =
  Object.freeze([
    { id: "Off", value: 0 },
    { id: "Quiet", value: 0.35 },
    { id: "Normal", value: 0.7 },
    { id: "Loud", value: 1 },
  ]);

/** The step a stored number belongs to, so a slider round-trips through JSON. */
export function nearestStep(value: number): number {
  let best = VOLUME_STEPS[0]!.value;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const s of VOLUME_STEPS) {
    const gap = Math.abs(s.value - value);
    if (gap < bestGap) { best = s.value; bestGap = gap; }
  }
  return best;
}

/** How far ahead of the clock the music scheduler works, in seconds. */
const LOOKAHEAD = 1.5;
/** How often it wakes up to do that, in milliseconds. */
const TICK_MS = 250;
/** Seconds of crossfade when the scene changes. */
const SCENE_FADE = 0.9;
/**
 * Where a recorded track sits on the music bus. The files are levelled to
 * -16 LUFS, which is far hotter than the synthesised bed was written to be,
 * and the cues were balanced against that bed; this is the one knob that puts
 * the recordings back under them.
 */
const TRACK_GAIN = 0.6;
/**
 * Decoded loops kept around. A two-minute stereo loop is ~50 MB of PCM once
 * decoded, so this is the map and the last town, not the whole soundtrack.
 */
const TRACK_CACHE = 2;
/**
 * Seconds before a pass ends that the next piece of a rotation is fetched and
 * decoded, so it is in the cache when the switch is scheduled. Decoding a
 * three-minute Vorbis file takes a second or two on the main thread.
 */
const PRELOAD_AHEAD = 20;

type AudioCtor = new () => AudioContext;

function contextCtor(): AudioCtor | null {
  const w = globalThis as unknown as {
    AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private uiBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private noise: AudioBuffer | null = null;

  private volumes: AudioVolumes = DEFAULT_VOLUMES;
  private scene: Scene | null = null;
  /** The recorded pieces the scene asked for; empty for the synthesised bed. */
  private playlist: Playlist = [];
  /** `playlist`, as one string: the identity a scene change is compared on. */
  private playlistKey = "";
  /**
   * Where each rotation is up to, by playlist key, so a trip into a town and
   * back moves the map on to its next piece rather than starting it over.
   */
  private readonly cursors = new Map<string, number>();
  /** The recorded piece sounding or about to, or null for the synthesised bed. */
  private track: Track | null = null;
  /**
   * The source actually sounding, with the gain node its fades ride on and the
   * timers that will rotate it out, if it is one piece of several.
   */
  private playing: {
    readonly url: string; readonly src: AudioBufferSourceNode; readonly gain: GainNode;
    readonly timers: ReturnType<typeof setTimeout>[];
  } | null = null;
  /** Decoded files, oldest first; see TRACK_CACHE. */
  private readonly decoded = new Map<string, AudioBuffer>();
  private readonly loads = new Map<string, Promise<AudioBuffer | null>>();
  /** Files that would not fetch or decode. The bed stands in for them, once, not on every scene change. */
  private readonly failed = new Set<string>();
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Context time the next bar starts at, and which bar it is. */
  private nextBarAt = 0;
  private barIndex = 0;
  private readonly lastCueAt = new Map<Cue, number>();
  private closed = false;

  constructor(volumes: AudioVolumes = DEFAULT_VOLUMES) {
    this.volumes = volumes;
  }

  // ------------------------------------------------------------- lifecycle

  get ready(): boolean { return this.ctx !== null && this.ctx.state === "running"; }
  get available(): boolean { return contextCtor() !== null; }

  /**
   * Open the context, or resume one the browser suspended.
   *
   * Safe to call on every click - which is how it is called, because there is
   * no reliable event for "the first gesture" and a browser may suspend a
   * running context again when the tab is backgrounded.
   */
  unlock(): void {
    if (this.closed) return;
    if (this.ctx === null) {
      const Ctor = contextCtor();
      if (Ctor === null) return;
      const ctx = new Ctor();
      this.ctx = ctx;
      this.noise = makeNoise(ctx);

      const master = ctx.createGain();
      master.connect(ctx.destination);
      this.master = master;
      this.sfxBus = ctx.createGain();
      this.uiBus = ctx.createGain();
      this.musicBus = ctx.createGain();
      for (const b of [this.sfxBus, this.uiBus, this.musicBus]) b.connect(master);

      this.applyVolumes(0);
      // A scene asked for before the context existed is remembered, not lost.
      if (this.scene !== null) this.resume();
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
  }

  close(): void {
    this.closed = true;
    this.stopScheduler();
    this.clearRotation();
    this.playing = null;
    const ctx = this.ctx;
    this.ctx = null;
    this.master = this.sfxBus = this.uiBus = this.musicBus = null;
    if (ctx !== null && ctx.state !== "closed") void ctx.close();
  }

  // --------------------------------------------------------------- volumes

  setVolumes(v: AudioVolumes): void {
    this.volumes = v;
    this.applyVolumes(0.08);
    // Turning the music to zero should stop the scheduler rather than keep
    // building a graph nobody can hear.
    if (v.music <= 0 || v.master <= 0) { this.stopScheduler(); this.stopTrack(); }
    else if (this.scene !== null && this.timer === null && this.playing === null) this.resume();
  }

  getVolumes(): AudioVolumes { return this.volumes; }

  private applyVolumes(ramp: number): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    const at = ctx.currentTime;
    const set = (g: GainNode | null, value: number): void => {
      if (g === null) return;
      if (ramp <= 0) g.gain.setValueAtTime(value, at);
      else {
        g.gain.cancelScheduledValues(at);
        g.gain.setValueAtTime(g.gain.value, at);
        g.gain.linearRampToValueAtTime(value, at + ramp);
      }
    };
    set(this.master, this.volumes.master);
    set(this.sfxBus, this.volumes.sfx);
    // The shell's own clicks ride under the game's sounds; a menu that is as
    // loud as a siege is a menu the player turns off.
    set(this.uiBus, this.volumes.sfx * 0.7);
    set(this.musicBus, this.volumes.music);
  }

  // ------------------------------------------------------------------ cues

  private busNode(bus: Bus): GainNode | null {
    return bus === "ui" ? this.uiBus : this.sfxBus;
  }

  /**
   * Play one cue, optionally a moment from now. Silent, not an error, when
   * there is no context - which is every headless run.
   */
  play(cue: Cue, delaySeconds = 0): void {
    const ctx = this.ctx;
    const noise = this.noise;
    if (ctx === null || noise === null || ctx.state !== "running") return;
    if (this.volumes.master <= 0 || this.volumes.sfx <= 0) return;
    const patch: Patch | undefined = PATCHES[cue];
    if (patch === undefined) return;
    const dest = this.busNode(BUS_OF[cue]);
    if (dest === null) return;
    // A hair in the future even at zero delay: scheduling at exactly
    // `currentTime` can land in a block the graph has already rendered, which
    // clips the attack off.
    playPatch(ctx, dest, patch, noise, ctx.currentTime + 0.005 + delaySeconds);
  }

  /**
   * Sound a drained batch of kernel events.
   *
   * `now` is the wall clock in milliseconds, and it is a parameter so that the
   * rate limiting is testable without an `AudioContext`; the shell passes
   * `performance.now()`.
   */
  playEvents(events: readonly GameEvent[], now: number): readonly CueHit[] {
    const hits = selectCues(events, now, this.lastCueAt);
    for (const h of hits) this.play(h.cue, h.delayMs / 1000);
    return hits;
  }

  /** How long the longest voice of a cue lasts; the shell uses it for stills. */
  static durationOf(cue: Cue): number {
    const p: Patch | undefined = PATCHES[cue];
    return p === undefined ? 0 : patchLength(p);
  }

  // ----------------------------------------------------------------- music

  /**
   * Ask for a piece, or a rotation of them. The change lands at the next bar
   * rather than immediately, because cutting a pad off mid-envelope is audible
   * and waiting at most one bar is not.
   *
   * Given several tracks the scene plays them in turn: each runs one full pass
   * and crossfades into the next, and coming back to the scene after another
   * one played moves on to the next piece too, so a player who keeps popping
   * into towns still hears the whole rotation. Asking for the same scene with
   * the same list is a no-op, which is what a re-render is.
   */
  setScene(scene: Scene | null, tracks: Track | Playlist | null = null): void {
    const playlist = toPlaylist(tracks);
    const key = playlist.map((t) => t.url).join("\n");
    if (this.scene === scene && this.playlistKey === key) return;
    this.scene = scene;
    this.playlist = playlist;
    this.playlistKey = key;
    this.track = this.advance();
    if (scene === null) { this.stopTrack(); this.fadeOutMusic(); return; }
    if (this.volumes.music <= 0) return;
    if (this.ctx === null) return;
    // A new piece starts on the next bar boundary the scheduler reaches; the
    // bar counter restarts so a piece always begins at its first chord.
    this.barIndex = 0;
    this.nextBarAt = 0;
    this.fadeOutMusic();
    this.stopTrack();
    this.resume();
  }

  getScene(): Scene | null { return this.scene; }
  /** The piece sounding or about to; in a rotation, the one the cursor is on. */
  getTrack(): Track | null { return this.track; }
  getPlaylist(): Playlist { return this.playlist; }

  /**
   * The next piece of the current playlist that is not known to be broken,
   * moving the rotation's cursor on to it; null when there is none, which is
   * the bed's cue. A one-track playlist always answers with its track, which
   * is why a lone recording still loops the way it did before rotations.
   */
  private advance(): Track | null {
    return this.pick(true);
  }

  /** What `advance` would answer, without moving the cursor. */
  private peek(): Track | null {
    return this.pick(false);
  }

  private pick(commit: boolean): Track | null {
    const list = this.playlist;
    const n = list.length;
    if (n === 0) return null;
    let i = this.cursors.get(this.playlistKey) ?? -1;
    for (let step = 0; step < n; step++) {
      i = (i + 1) % n;
      const t = list[i];
      if (t === undefined || this.failed.has(t.url)) continue;
      if (commit) this.cursors.set(this.playlistKey, i);
      return t;
    }
    return null;
  }

  /**
   * Start whichever kind of piece the scene calls for: the recording, or the
   * synthesised bed when there is none or it could not be loaded.
   */
  private resume(): void {
    let track = this.track;
    if (track !== null && this.failed.has(track.url)) track = this.track = this.advance();
    if (track !== null) {
      this.stopScheduler();
      void this.playTrack(track);
    } else {
      this.startScheduler();
    }
  }

  /**
   * Sound a piece, now or at a context time - which is how a rotation hands
   * over: the next piece starts exactly where the pass ends, and the outgoing
   * one fades out from that same moment. An `at` already in the past means the
   * timer that asked was late (a throttled background tab); the switch then
   * simply lands now, as a crossfade mid-pass, and the outgoing piece has been
   * looping seamlessly in the meantime.
   */
  private async playTrack(track: Track, at = 0): Promise<void> {
    const buf = await this.load(track.url);
    const ctx = this.ctx;
    const bus = this.musicBus;
    if (this.closed || ctx === null || bus === null) return;
    if (this.track?.url !== track.url) return; // superseded while it loaded
    if (this.volumes.music <= 0 || this.volumes.master <= 0) return;
    if (this.playing?.url === track.url) return;
    if (buf === null) { this.resume(); return; } // the bed, or the next piece, takes over
    const start = at > ctx.currentTime ? at : ctx.currentTime + 0.005;
    this.stopTrack(start);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.loopStart = 0;
    // The file carries a second of its own opening after the loop point, so
    // the wrap lands on the next sample whatever the decoder did with the tail.
    src.loopEnd = Math.min(track.loopSeconds, buf.duration);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(TRACK_GAIN, start + SCENE_FADE);
    src.connect(gain);
    gain.connect(bus);
    src.start(start);
    this.playing = { url: track.url, src, gain, timers: [] };
    this.armRotation(start, track);
  }

  /**
   * Book the hand-over to the next piece, when there is one. Two timers: one
   * fetches and decodes the next file well ahead, one schedules the switch a
   * lookahead before the pass ends. Both are wall-clock timers aimed at a
   * context time, and only the context time is trusted: a timer that fires
   * early (the context was suspended, so its clock stood still) schedules the
   * switch at the right moment anyway, and one that fires late crossfades on
   * arrival. The source keeps `loop` on throughout, so a late timer means a
   * longer pass and never a gap.
   */
  private armRotation(start: number, track: Track): void {
    const ctx = this.ctx;
    const playing = this.playing;
    if (ctx === null || playing === null || this.playlist.length < 2) return;
    const endAt = start + track.loopSeconds;
    const msUntil = (t: number): number => Math.max(0, (t - ctx.currentTime) * 1000);
    playing.timers.push(setTimeout(() => {
      const next = this.peek();
      if (next !== null && next.url !== playing.url) void this.load(next.url);
    }, msUntil(endAt - PRELOAD_AHEAD)));
    playing.timers.push(setTimeout(() => { this.rotate(endAt); }, msUntil(endAt - LOOKAHEAD)));
  }

  /** The pass is ending: move the cursor on and start what it lands on. */
  private rotate(at: number): void {
    if (this.closed || this.scene === null) return;
    const next = this.advance();
    this.track = next;
    if (next === null) { this.stopTrack(); this.startScheduler(); return; }
    if (next.url === this.playing?.url) { this.playing.timers.length = 0; this.armRotation(at, next); return; }
    void this.playTrack(next, at);
  }

  private clearRotation(): void {
    const p = this.playing;
    if (p === null) return;
    for (const t of p.timers) clearTimeout(t);
    p.timers.length = 0;
  }

  /**
   * Fade the sounding track out from `at` (now, by default) and let it go;
   * safe to call when nothing plays.
   */
  private stopTrack(at?: number): void {
    const p = this.playing;
    const ctx = this.ctx;
    this.clearRotation();
    this.playing = null;
    if (p === null || ctx === null) return;
    const from = at === undefined || at < ctx.currentTime ? ctx.currentTime : at;
    p.gain.gain.cancelScheduledValues(from);
    p.gain.gain.setValueAtTime(p.gain.gain.value, from);
    p.gain.gain.linearRampToValueAtTime(0.0001, from + SCENE_FADE);
    try { p.src.stop(from + SCENE_FADE); } catch { /* never started */ }
  }

  /**
   * Fetch and decode a file, once. Concurrent asks share the flight, hits
   * move to the back of the cache, and a failure is remembered so the bed
   * plays instead of the page retrying on every town the player opens.
   */
  private load(url: string): Promise<AudioBuffer | null> {
    const hit = this.decoded.get(url);
    if (hit !== undefined) {
      this.decoded.delete(url);
      this.decoded.set(url, hit);
      return Promise.resolve(hit);
    }
    const inFlight = this.loads.get(url);
    if (inFlight !== undefined) return inFlight;
    const ctx = this.ctx;
    if (ctx === null) return Promise.resolve(null);
    const flight = (async (): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${url}: HTTP ${String(res.status)}`);
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        this.decoded.set(url, buf);
        while (this.decoded.size > TRACK_CACHE) {
          const oldest = this.decoded.keys().next().value;
          if (oldest === undefined) break;
          this.decoded.delete(oldest);
        }
        return buf;
      } catch (err) {
        // A missing file, or a browser whose decoder has no Vorbis (Safari):
        // not the player's problem. The synthesised bed plays instead.
        console.warn(`music: ${url} did not load; playing the synthesised bed`, err);
        this.failed.add(url);
        return null;
      } finally {
        this.loads.delete(url);
      }
    })();
    this.loads.set(url, flight);
    return flight;
  }

  private fadeOutMusic(): void {
    const ctx = this.ctx;
    const bus = this.musicBus;
    if (ctx === null || bus === null) return;
    const at = ctx.currentTime;
    bus.gain.cancelScheduledValues(at);
    bus.gain.setValueAtTime(bus.gain.value, at);
    bus.gain.linearRampToValueAtTime(0.0001, at + SCENE_FADE);
    bus.gain.linearRampToValueAtTime(this.volumes.music, at + SCENE_FADE * 2);
  }

  private startScheduler(): void {
    if (this.timer !== null || this.ctx === null) return;
    this.timer = setInterval(() => { this.pump(); }, TICK_MS);
    this.pump();
  }

  private stopScheduler(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Schedule every bar that starts inside the lookahead window.
   *
   * This is the standard Web Audio pattern and the reason for it is worth
   * stating: `setInterval` is a millisecond or two late whenever the main
   * thread is busy, and the main thread here is running a Pixi renderer. Notes
   * placed at "now" would therefore jitter audibly. Notes placed at an absolute
   * context time a second and a half ahead do not, and the timer only has to be
   * accurate enough to wake up before the window empties.
   */
  private pump(): void {
    const ctx = this.ctx;
    const bus = this.musicBus;
    const noise = this.noise;
    const scene = this.scene;
    if (ctx === null || bus === null || noise === null || scene === null) return;
    if (ctx.state !== "running") return;

    const score = SCORES[scene];
    const now = ctx.currentTime;
    if (this.nextBarAt < now) this.nextBarAt = now + 0.05;

    while (this.nextBarAt < now + LOOKAHEAD) {
      playPatch(ctx, bus, bar(score, this.barIndex), noise, this.nextBarAt);
      this.barIndex += 1;
      this.nextBarAt += score.secondsPerBar;
    }
  }
}
